'use client'

import React, { useEffect, useRef } from 'react'
import { createStore } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { DocumentPage, Recipient, File, TemplateField } from 'database'
import { CanvasBlock } from '../constants/blocks'
import { useMediaQuery } from '@uidotdev/usehooks'
import { getFieldIsSignature, resortElements } from 'bff'

type NonFunctionPropertyNames<T> = {
  [K in keyof T as T[K] extends Function ? never : K]: T[K]
}

export type PDField = Omit<TemplateField, 'template_id'>

export interface PagePDF extends Omit<DocumentPage, 'file_id'> {
  file: {
    url: never
  }
}

export interface PDFStoreProvidedProps {
  debug?: boolean
  zoom?: number
  headerHeight: `${number}rem`
  mode: 'edit' | 'view'
  fields: PDField[]
  recipients: Recipient[]
  documents: {
    index: number
    id: string
    pages: Array<PagePDF>
    file: Pick<File, 'original_name'> & {
      url: never
    }
  }[]
}

export interface PageBoundary {
  width: number
  height: number
  left: number
  top: number
  x: number
  y: number
  pageIndex: number
  originalPageWidth: number
  originalPageHeight: number
}

export interface PDFStoreProps extends PDFStoreProvidedProps {
  isIntersectingPage?: boolean
  scrollLeft: number
  scrollTop: number
  scrollableContainerContentHeight: number
  isAlertDialogRecipientFieldsOpen?: boolean
  isLargeDevice?: boolean
  isDocumentReady?: boolean
  currentFocusFieldId?: string
  currentEditingFieldId?: string
  currentRecipientId: string
  boundaries: Record<string, PageBoundary>
  focusedBlock?: CanvasBlock
  zoom: number
  removeCurrentEditingField: () => void
  blurCurrentEditingField: () => void
  setPageBoundary: (pageId: string, boundary: PageBoundary) => void
  updateField: (id: string, field: Partial<PDField>) => void
  removeField: (id: string | string[], force?: boolean) => void
  addField: (field: PDField) => void
  addBatchFields: (fields: PDField[]) => void
  update: <T extends keyof NonFunctionPropertyNames<PDFStoreProps>>(
    key: T,
    value: NonFunctionPropertyNames<PDFStoreProps>[T]
  ) => void
  batchUpdate: (
    partial: Partial<NonFunctionPropertyNames<PDFStoreProps>>
  ) => void
  computed: {
    currentFocusField?: PDField
    currentRecipientFields: PDField[]
  }
  currentViewportPageId?: string
  currentViewportDocumentId?: string
}

export const createPDFStore = (props: PDFStoreProvidedProps) => {
  return createStore<PDFStoreProps>()(
    devtools((set, get) => ({
      ...props,
      documents: resortElements(props.documents),
      debug: props.debug,
      scrollLeft: 0,
      scrollTop: 0,
      scrollableContainerContentHeight: 0,
      computed: {
        get currentRecipientFields() {
          return get()?.fields.filter(
            (field) => field.recipient_id === get()?.currentRecipientId
          )
        },
        get currentFocusField() {
          return get()?.fields.find(
            (field) => field.id === get()?.currentFocusFieldId
          )
        },
      },
      removeCurrentEditingField: () => {
        const store = get()

        const currentEditingField = store?.currentEditingFieldId

        if (currentEditingField) {
          store.removeField(currentEditingField)
        }
      },
      currentRecipientId: props.recipients[0]?.id,
      batchUpdate: (partial) => set(partial),
      blurCurrentEditingField: () =>
        set((state) => {
          if (state.isAlertDialogRecipientFieldsOpen) {
            return {}
          }

          return {
            currentEditingFieldId: undefined,
            isAlertDialogRecipientFieldsOpen: false,
          }
        }),
      updateField: (id, updateState) =>
        set((state) => ({
          fields: state.fields.map((field, fieldIndex) => {
            if (field.id !== id) return field

            return {
              ...field,
              ...updateState,
            }
          }),
        })),
      removeField: (fieldIdToRemove, force) =>
        set((state) => {
          const fields = Array.isArray(fieldIdToRemove)
            ? fieldIdToRemove
            : [fieldIdToRemove]

          return {
            currentEditingFieldId: undefined,
            fields: state.fields.filter((field) => !fields.includes(field.id)),
          }
        }),
      addField: (field) => {
        set((state) => ({
          fields: [...state.fields, field],
          focusedBlock: undefined,
        }))
      },
      addBatchFields: (fields: PDField[]) => {
        set((state) => ({
          fields: [...state.fields, ...fields],
        }))
      },
      fields: props.fields,
      setPageBoundary: (pageId, boundary) =>
        set((state) => ({
          boundaries: {
            ...state.boundaries,
            [pageId]: boundary,
          },
        })),
      boundaries: {},
      zoom: typeof props.zoom === 'number' ? props.zoom : 1,
      update: (key, value) =>
        set({
          [key]: value,
        }),
    }))
  )
}

type PDFStore = ReturnType<typeof createPDFStore>

export const PDFContext = React.createContext<PDFStore | undefined>(undefined)

export const usePDF = () => React.useContext(PDFContext)!

export const PDFProvider = ({
  children,
  ...props
}: { children: React.ReactNode } & PDFStoreProvidedProps) => {
  const isLargeDevice = useMediaQuery('only screen and (min-width: 768px)')
  const store = useRef(
    createPDFStore({
      ...props,
      zoom: isLargeDevice ? 0.75 : 1,
    })
  ).current

  useEffect(() => {
    store.setState({
      isLargeDevice,
    })
  }, [isLargeDevice])

  return <PDFContext.Provider value={store}>{children}</PDFContext.Provider>
}

export const usePDFStore = <T,>(
  selector: (store: PDFStoreProps) => T,
  equalityFn?: ((a: T, b: T) => boolean) | undefined
) => useStoreWithEqualityFn(usePDF()!, selector, equalityFn)
